/**
 * One fetch wrapper for the Gallop API. Cookies carry the Better Auth session (credentials: include);
 * the base URL is same-origin `/api` in dev (Vite proxy) and the Fly host on staging.
 */
export const API_URL: string = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api'

export type UnauthReason = 'no_session' | 'no_seat' | 'seat_deactivated' | 'mfa_enrollment_required' | 'mfa_reenroll_required' | 'principal_disabled'

export class ApiError extends Error {
  constructor(readonly status: number, readonly body: Record<string, unknown>) {
    super(typeof body.message === 'string' ? body.message : `API ${status}`)
  }
}
export class UnauthenticatedError extends ApiError {
  constructor(status: number, body: Record<string, unknown>, readonly reason: UnauthReason) {
    super(status, body)
  }
}
export class StepUpRequiredError extends ApiError {
  constructor(status: number, body: Record<string, unknown>, readonly tier: 'tier1' | 'tier2', readonly action: string) {
    super(status, body)
  }
}

type Listener = (err: UnauthenticatedError) => void
const unauthListeners = new Set<Listener>()
/** The shell subscribes once and routes to /signin with the plain reason (PRD-018 §3.10). */
export function onUnauthenticated(fn: Listener): () => void {
  unauthListeners.add(fn)
  return () => unauthListeners.delete(fn)
}

export async function api<T = unknown>(path: string, init: RequestInit & { json?: unknown, idempotencyKey?: string } = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.json !== undefined)
    headers.set('content-type', 'application/json')
  if (init.idempotencyKey)
    headers.set('idempotency-key', init.idempotencyKey)
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    body: init.json !== undefined ? JSON.stringify(init.json) : init.body ?? null,
    headers,
    credentials: 'include',
  })
  const text = await res.text()
  const body = (text ? safeJson(text) : {}) as Record<string, unknown>
  if (res.ok)
    return body as T
  if (res.status === 401 && typeof body.reason === 'string') {
    const err = new UnauthenticatedError(res.status, body, body.reason as UnauthReason)
    for (const l of unauthListeners) l(err)
    throw err
  }
  if (res.status === 403 && body.error === 'step_up_required')
    throw new StepUpRequiredError(res.status, body, body.tier as 'tier1' | 'tier2', String(body.action ?? ''))
  throw new ApiError(res.status, body)
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  }
  catch {
    return { message: text }
  }
}
